import { createContext, useContext, useReducer, useCallback, useRef } from "react";
import {
    bandiApi,
    conversationsApi,
    documentsApi,
    streamChatMessage,
} from "../../services/ragApi";

// Decode JWT payload (no verification - solo per ricavare id/role lato client per UX gating).
// Il backend resta authoritative.
function decodeCurrentUser() {
    try {
        const token = localStorage.getItem("token");
        if (!token) return { id: null, role: null };
        const payload = JSON.parse(atob(token.split(".")[1]));
        return {
            id: typeof payload.id === "number" ? payload.id : null,
            role: typeof payload.role === "string" ? payload.role : localStorage.getItem("role"),
        };
    } catch {
        return { id: null, role: localStorage.getItem("role") };
    }
}

// State shape:
// {
//   bandi: Bando[],
//   currentBandoId: string | null,
//   conversations: Conversation[],
//   currentConversationId: string | null,
//   messages: Message[],
//   streaming: { isStreaming, partialContent, partialSources },
//   documents: Document[],
//   loading: { bandi, conversations, messages, documents },
//   error: string | null,
// }

const initialState = {
    bandi: [],
    currentBandoId: null,
    members: [],
    invitableUsers: [],
    conversations: [],
    currentConversationId: null,
    messages: [],
    streaming: { isStreaming: false, partialContent: "", partialSources: null },
    documents: [],
    loading: {
        bandi: false,
        conversations: false,
        messages: false,
        documents: false,
        members: false,
        invitableUsers: false,
    },
    error: null,
};

function reducer(state, action) {
    switch (action.type) {
        case "BANDI_LOADED":
            return { ...state, bandi: action.payload, loading: { ...state.loading, bandi: false } };
        case "BANDO_SELECTED":
            // Cambiando bando, resettiamo conversation/messages/documents/members.
            return {
                ...state,
                currentBandoId: action.payload,
                currentConversationId: null,
                conversations: [],
                messages: [],
                documents: [],
                members: [],
                invitableUsers: [],
            };
        case "MEMBERS_LOADED":
            return {
                ...state,
                members: action.payload,
                loading: { ...state.loading, members: false },
            };
        case "INVITABLE_USERS_LOADED":
            return {
                ...state,
                invitableUsers: action.payload,
                loading: { ...state.loading, invitableUsers: false },
            };
        case "CONVERSATIONS_LOADED":
            return {
                ...state,
                conversations: action.payload,
                loading: { ...state.loading, conversations: false },
            };
        case "CONVERSATION_SELECTED":
            return { ...state, currentConversationId: action.payload, messages: [] };
        case "CONVERSATION_CREATED":
            return {
                ...state,
                conversations: [action.payload, ...state.conversations],
                currentConversationId: action.payload.id,
                messages: [],
            };
        case "CONVERSATION_DELETED": {
            const filtered = state.conversations.filter((c) => c.id !== action.payload);
            return {
                ...state,
                conversations: filtered,
                currentConversationId:
                    state.currentConversationId === action.payload ? null : state.currentConversationId,
                messages: state.currentConversationId === action.payload ? [] : state.messages,
            };
        }
        case "MESSAGES_LOADED":
            return { ...state, messages: action.payload, loading: { ...state.loading, messages: false } };
        case "MESSAGE_APPENDED":
            return { ...state, messages: [...state.messages, action.payload] };
        case "STREAM_STARTED":
            return {
                ...state,
                streaming: { isStreaming: true, partialContent: "", partialSources: null },
            };
        case "STREAM_TOKEN":
            return {
                ...state,
                streaming: {
                    ...state.streaming,
                    partialContent: state.streaming.partialContent + action.payload,
                },
            };
        case "STREAM_SOURCES":
            return { ...state, streaming: { ...state.streaming, partialSources: action.payload } };
        case "STREAM_DONE": {
            const assistantMsg = {
                id: action.payload.messageId,
                conversationId: state.currentConversationId,
                role: "assistant",
                content: state.streaming.partialContent,
                sources: state.streaming.partialSources,
                createdAt: new Date().toISOString(),
            };
            return {
                ...state,
                messages: [...state.messages, assistantMsg],
                streaming: { isStreaming: false, partialContent: "", partialSources: null },
            };
        }
        case "STREAM_ERROR":
            return {
                ...state,
                streaming: { isStreaming: false, partialContent: "", partialSources: null },
                error: action.payload,
            };
        case "DOCUMENTS_LOADED":
            return {
                ...state,
                documents: action.payload,
                loading: { ...state.loading, documents: false },
            };
        case "DOCUMENT_APPENDED":
            return { ...state, documents: [action.payload, ...state.documents] };
        case "DOCUMENT_UPDATED": {
            const updated = state.documents.map((d) =>
                d.id === action.payload.id ? action.payload : d,
            );
            return { ...state, documents: updated };
        }
        case "DOCUMENT_DELETED":
            return {
                ...state,
                documents: state.documents.filter((d) => d.id !== action.payload),
            };
        case "LOADING_SET":
            return { ...state, loading: { ...state.loading, [action.payload.key]: action.payload.value } };
        case "ERROR_SET":
            return { ...state, error: action.payload };
        default:
            return state;
    }
}

const RagContext = createContext(null);

export function RagProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    // currentUser e' calcolato ad ogni render leggendo il token corrente da
    // localStorage. NON puo' stare nello state perche' useReducer chiama
    // initialState una sola volta al mount: dopo un cambio utente senza full
    // page reload (es. logout/login via React Router), currentUser nello state
    // resterebbe del primo utente -> gating delete documenti sbagliato.
    const currentUser = decodeCurrentUser();
    // Ref per evitare double-stream se streamChat viene chiamato due volte rapidamente
    const streamingRef = useRef(false);

    const loadBandi = useCallback(async () => {
        dispatch({ type: "LOADING_SET", payload: { key: "bandi", value: true } });
        try {
            const resp = await bandiApi.list();
            dispatch({ type: "BANDI_LOADED", payload: resp.data || [] });
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nel caricamento bandi" });
            dispatch({ type: "LOADING_SET", payload: { key: "bandi", value: false } });
        }
    }, []);

    const selectBando = useCallback((bandoId) => {
        dispatch({ type: "BANDO_SELECTED", payload: bandoId });
    }, []);

    const createBando = useCallback(
        async (name, description) => {
            try {
                const resp = await bandiApi.create({ name, description });
                await loadBandi();
                return resp.data;
            } catch (err) {
                dispatch({ type: "ERROR_SET", payload: err.message || "Errore nella creazione del bando" });
                throw err;
            }
        },
        [loadBandi],
    );

    const deleteBando = useCallback(
        async (bandoId) => {
            try {
                await bandiApi.delete(bandoId);
                // Se stavamo guardando questo bando, deselezionalo (resetta anche
                // conversation/messages/documents tramite l'azione BANDO_SELECTED).
                dispatch({ type: "BANDO_SELECTED", payload: null });
                await loadBandi();
            } catch (err) {
                dispatch({ type: "ERROR_SET", payload: err.message || "Errore nell'eliminazione del bando" });
                throw err;
            }
        },
        [loadBandi],
    );

    const loadConversations = useCallback(async (bandoId) => {
        dispatch({ type: "LOADING_SET", payload: { key: "conversations", value: true } });
        try {
            const resp = await conversationsApi.list(bandoId);
            dispatch({ type: "CONVERSATIONS_LOADED", payload: resp.data || [] });
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nel caricamento conversazioni" });
            dispatch({ type: "LOADING_SET", payload: { key: "conversations", value: false } });
        }
    }, []);

    const selectConversation = useCallback(async (conversationId) => {
        dispatch({ type: "CONVERSATION_SELECTED", payload: conversationId });
        if (!conversationId) return;
        dispatch({ type: "LOADING_SET", payload: { key: "messages", value: true } });
        try {
            const resp = await conversationsApi.get(conversationId);
            dispatch({ type: "MESSAGES_LOADED", payload: resp.data?.messages || [] });
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nel caricamento messaggi" });
            dispatch({ type: "LOADING_SET", payload: { key: "messages", value: false } });
        }
    }, []);

    const createConversation = useCallback(async (bandoId, title) => {
        try {
            const resp = await conversationsApi.create({ bandoId, title });
            dispatch({ type: "CONVERSATION_CREATED", payload: resp.data });
            return resp.data;
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nella creazione conversazione" });
            throw err;
        }
    }, []);

    const deleteConversation = useCallback(async (conversationId) => {
        try {
            await conversationsApi.delete(conversationId);
            dispatch({ type: "CONVERSATION_DELETED", payload: conversationId });
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nell'eliminazione" });
        }
    }, []);

    const sendMessage = useCallback(async (conversationId, message) => {
        if (streamingRef.current) return;
        streamingRef.current = true;

        const userMsg = {
            id: `tmp-${Date.now()}`,
            conversationId,
            role: "user",
            content: message,
            sources: null,
            createdAt: new Date().toISOString(),
        };
        dispatch({ type: "MESSAGE_APPENDED", payload: userMsg });
        dispatch({ type: "STREAM_STARTED" });

        try {
            for await (const event of streamChatMessage(conversationId, message)) {
                if (event.type === "token") {
                    dispatch({ type: "STREAM_TOKEN", payload: event.data });
                } else if (event.type === "sources" || event.type === "cited_sources") {
                    dispatch({ type: "STREAM_SOURCES", payload: event.data });
                } else if (event.type === "done") {
                    dispatch({ type: "STREAM_DONE", payload: event.data });
                } else if (event.type === "error") {
                    dispatch({ type: "STREAM_ERROR", payload: event.data });
                }
            }
        } catch (err) {
            dispatch({ type: "STREAM_ERROR", payload: err.message || "Errore durante lo streaming" });
        } finally {
            streamingRef.current = false;
        }
    }, []);

    // Documents
    const loadDocuments = useCallback(async (bandoId) => {
        dispatch({ type: "LOADING_SET", payload: { key: "documents", value: true } });
        try {
            const resp = await documentsApi.list(bandoId);
            dispatch({ type: "DOCUMENTS_LOADED", payload: resp.data || [] });
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nel caricamento documenti" });
            dispatch({ type: "LOADING_SET", payload: { key: "documents", value: false } });
        }
    }, []);

    // Upload con append OPTIMISTICO al state (status processing): la riga compare
    // subito in tabella e il polling rilevera' lo switch a ready quando arriva.
    const uploadDocument = useCallback(async (file, options) => {
        try {
            const resp = await documentsApi.upload(file, options);
            const doc = resp.data;
            // BigInt fileSize arriva come stringa dal backend; lo conserviamo cosi'.
            dispatch({ type: "DOCUMENT_APPENDED", payload: doc });
            return doc;
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nell'upload" });
            throw err;
        }
    }, []);

    const deleteDocument = useCallback(async (documentId) => {
        try {
            await documentsApi.delete(documentId);
            dispatch({ type: "DOCUMENT_DELETED", payload: documentId });
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nell'eliminazione documento" });
        }
    }, []);

    // Members
    const loadMembers = useCallback(async (bandoId) => {
        if (!bandoId) return;
        dispatch({ type: "LOADING_SET", payload: { key: "members", value: true } });
        try {
            const resp = await bandiApi.listMembers(bandoId);
            dispatch({ type: "MEMBERS_LOADED", payload: resp.data || [] });
        } catch (err) {
            dispatch({ type: "ERROR_SET", payload: err.message || "Errore nel caricamento membri" });
            dispatch({ type: "LOADING_SET", payload: { key: "members", value: false } });
        }
    }, []);

    const loadInvitableUsers = useCallback(async (bandoId) => {
        if (!bandoId) return;
        dispatch({ type: "LOADING_SET", payload: { key: "invitableUsers", value: true } });
        try {
            const resp = await bandiApi.listInvitableUsers(bandoId);
            dispatch({ type: "INVITABLE_USERS_LOADED", payload: resp.data || [] });
        } catch (err) {
            dispatch({
                type: "ERROR_SET",
                payload: err.response?.data?.error || err.message || "Errore nel caricamento utenti invitabili",
            });
            dispatch({ type: "LOADING_SET", payload: { key: "invitableUsers", value: false } });
        }
    }, []);

    const inviteMember = useCallback(
        async (bandoId, userId) => {
            try {
                await bandiApi.inviteMember(bandoId, userId);
                await loadMembers(bandoId);
                await loadInvitableUsers(bandoId);
            } catch (err) {
                dispatch({
                    type: "ERROR_SET",
                    payload: err.response?.data?.error || err.message || "Errore nell'invito",
                });
                throw err;
            }
        },
        [loadMembers, loadInvitableUsers],
    );

    const removeMember = useCallback(
        async (bandoId, userId) => {
            try {
                await bandiApi.removeMember(bandoId, userId);
                await loadMembers(bandoId);
                await loadInvitableUsers(bandoId);
            } catch (err) {
                dispatch({
                    type: "ERROR_SET",
                    payload: err.response?.data?.error || err.message || "Errore nella rimozione",
                });
                throw err;
            }
        },
        [loadMembers, loadInvitableUsers],
    );

    const clearError = useCallback(() => {
        dispatch({ type: "ERROR_SET", payload: null });
    }, []);

    // Membership corrente: 'owner' | 'member' | null. Calcolata sui membri caricati.
    const currentBandoMembership = (() => {
        if (!currentUser?.id || !state.currentBandoId) return null;
        const m = state.members.find((x) => x.userId === currentUser.id);
        return m?.role ?? null;
    })();

    const isSuperadmin = currentUser?.role === "superadmin";
    const canManageMembers = isSuperadmin || currentBandoMembership === "owner";

    // canManageDocument: stessa terna del backend delete + setVisibility
    // (uploader | owner del bando | superadmin). Usato come gating UI per
    // pulsante delete documento e per icona "Gestisci visibilita'".
    const canManageDocument = (doc) => {
        if (!doc) return false;
        if (isSuperadmin) return true;
        if (currentBandoMembership === "owner") return true;
        return currentUser?.id != null && doc.uploadedById === currentUser.id;
    };

    const value = {
        ...state,
        currentUser,
        currentBandoMembership,
        canManageMembers,
        canManageDocument,
        isSuperadmin,
        loadBandi,
        selectBando,
        createBando,
        deleteBando,
        loadConversations,
        selectConversation,
        createConversation,
        deleteConversation,
        sendMessage,
        loadDocuments,
        uploadDocument,
        deleteDocument,
        loadMembers,
        loadInvitableUsers,
        inviteMember,
        removeMember,
        clearError,
    };

    return <RagContext.Provider value={value}>{children}</RagContext.Provider>;
}

export function useRag() {
    const ctx = useContext(RagContext);
    if (!ctx) {
        throw new Error("useRag deve essere usato dentro <RagProvider>");
    }
    return ctx;
}
